import availableLanguages from '../../resources/languages.json';

export const LOCALE_SEGMENT_REGEX = /^\/([a-z]{2})(?=\/|$)/i;

const SUPPORTED_LOCALES = availableLanguages as readonly string[];

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = 'en';

const hasLeadingSlash = (path: string) => path.startsWith('/');
const ensureLeadingSlash = (path: string) =>
  hasLeadingSlash(path) ? path : `/${path}`;

const ensureTrailingSlash = (path: string) => {
  if (path === '/' || path.includes('.')) {
    return path;
  }

  // Handle query strings and fragments: add trailing slash to pathname only
  const queryIndex = path.indexOf('?');
  const hashIndex = path.indexOf('#');
  const separatorIndex =
    queryIndex >= 0 && hashIndex >= 0
      ? Math.min(queryIndex, hashIndex)
      : queryIndex >= 0
        ? queryIndex
        : hashIndex;

  if (separatorIndex >= 0) {
    const pathname = path.slice(0, separatorIndex);
    const rest = path.slice(separatorIndex);
    if (pathname.endsWith('/')) {
      return path;
    }
    return `${pathname}/${rest}`;
  }

  if (path.endsWith('/')) {
    return path;
  }
  return `${path}/`;
};

export const isSupportedLocale = (locale: string): locale is SupportedLocale =>
  SUPPORTED_LOCALES.includes(locale as SupportedLocale);

export const normalizeLocale = (locale?: string | null): SupportedLocale => {
  if (!locale) {
    return DEFAULT_LOCALE;
  }
  const normalized = locale.toLowerCase();
  return isSupportedLocale(normalized) ? normalized : DEFAULT_LOCALE;
};

export const getSupportedLocales = (): readonly SupportedLocale[] =>
  SUPPORTED_LOCALES;

export const extractLocaleFromPath = (
  path: string,
): SupportedLocale | undefined => {
  const match = path.match(LOCALE_SEGMENT_REGEX);
  if (!match) {
    return undefined;
  }
  const candidate = match[1].toLowerCase();
  return isSupportedLocale(candidate) ? candidate : undefined;
};

export const stripLocaleFromPath = (path: string): string => {
  const match = path.match(LOCALE_SEGMENT_REGEX);
  if (!match) {
    return ensureTrailingSlash(ensureLeadingSlash(path || '/'));
  }

  const without = path.slice(match[0].length) || '/';
  return ensureTrailingSlash(ensureLeadingSlash(without));
};

export const applyLocaleToPath = (path: string, locale: string): string => {
  const normalized = normalizeLocale(locale);
  const canonicalBase = stripLocaleFromPath(path);

  if (normalized === DEFAULT_LOCALE) {
    return canonicalBase;
  }

  const suffix = canonicalBase === '/' ? '' : canonicalBase;
  const localized = `${ensureLeadingSlash(normalized)}${suffix}`;
  return ensureTrailingSlash(localized);
};
